/**
 * Input component overrides: text fields, selects, checkboxes, radios, sliders, switches.
 * @see https://mui.com/material-ui/components/text-fields/
 * @see https://mui.com/material-ui/components/selects/
 * @see https://mui.com/material-ui/components/checkboxes/
 * @see https://mui.com/material-ui/components/radio-buttons/
 * @see https://mui.com/material-ui/components/slider/
 * @see https://mui.com/material-ui/components/switches/
 */
import varnishTokens from '@allenai/varnish-theme/mui';
import type { Components, Theme } from '@mui/material';

export const inputComponents: Components<Omit<Theme, 'components'>> = {
    MuiAutocomplete: {
        styleOverrides: {
            paper: ({ theme }) => ({
                borderRadius: varnishTokens.radii.sm.value,
                backgroundColor: (theme.vars ?? theme).palette.elements.overlay.background,
            }),
            option: { fontSize: varnishTokens.font.size.sm.value },
            clearIndicator: { borderRadius: varnishTokens.radii.sm.value },
            popupIndicator: { borderRadius: varnishTokens.radii.sm.value },
        },
    },
    MuiCheckbox: {
        styleOverrides: {
            root: ({ ownerState, theme }) => {
                const { palette } = theme.vars ?? theme;
                const colorKey = ownerState.color || 'primary';
                const p = palette[colorKey as keyof typeof palette] as
                    | { contrastText?: string }
                    | undefined;
                return {
                    padding: 0,
                    borderRadius: varnishTokens.radii.xs.value,
                    '& .MuiSvgIcon-root': { borderRadius: varnishTokens.radii.xs.value },
                    // Set background on checked SVG so the checkmark cutout shows the contrast color.
                    // The MUI checkbox SVG path is inset ~12.5% from the viewBox edges (path starts
                    // at 3/24), so we clip the background to the checkbox shape area to avoid a
                    // visible border around the checkbox.
                    '&.Mui-checked .MuiSvgIcon-root': p?.contrastText
                        ? { backgroundColor: p.contrastText, clipPath: 'inset(12.5% round 8.33%)' }
                        : {},
                };
            },
            sizeSmall: {
                '& .MuiSvgIcon-root': { fontSize: varnishTokens.font.size.md.value },
            },
            sizeMedium: {
                '& .MuiSvgIcon-root': { fontSize: varnishTokens.font.size.xl.value },
            },
        },
        variants: [
            {
                props: { color: 'tertiary' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.tertiary.main,
                        '&.Mui-checked': { color: palette.tertiary.main },
                    };
                },
            },
            {
                props: { color: 'default' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.form.checkbox.stroke,
                        '&.Mui-checked': { color: palette.form.checkbox.checked.fill },
                        '&.Mui-checked .MuiSvgIcon-root': {
                            backgroundColor: palette.form.checkbox.checked.contrast,
                            clipPath: 'inset(12.5% round 8.33%)',
                        },
                        '&.Mui-disabled': { color: palette.form.checkbox.disabled.stroke },
                        '&.Mui-disabled.Mui-checked': {
                            color: palette.form.checkbox.disabled.fill,
                        },
                        '&.Mui-error': { color: palette.form.checkbox.error.stroke },
                    };
                },
            },
            {
                props: { size: 'large' },
                style: {
                    '& .MuiSvgIcon-root': {
                        fontSize: '1.75rem', // we don't have a font size this size in the theme, consider adding?
                    },
                },
            },
        ],
    },
    MuiFilledInput: {
        defaultProps: {
            disableUnderline: true,
        },
        styleOverrides: {
            root: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    borderRadius: varnishTokens.radii.sm.value,
                    border: '1px solid transparent',
                    backgroundColor: palette.form.input.fill,
                    '&:hover': {
                        backgroundColor: palette.form.input.hovered.fill,
                        borderColor: palette.form.input.hovered.stroke,
                    },
                    '&.Mui-focused': { backgroundColor: palette.form.input.fill },
                    '&.Mui-error': { borderColor: palette.form.input.error.stroke },
                    '&.Mui-disabled': {
                        backgroundColor: palette.form.input.disabled.fill,
                        borderColor: palette.form.input.disabled.stroke,
                        cursor: 'not-allowed',
                    },
                    // Multiline (TextArea) padding on root
                    // Padding compensates for 1px border: visual size = padding + border
                    '&.MuiInputBase-multiline': { padding: '7px 11px' },
                    '&.MuiInputBase-multiline.MuiInputBase-sizeSmall': { padding: '11px' },
                };
            },
            // Padding compensates for 1px border: visual size = padding + border
            input: { padding: '7px 11px' },
            sizeSmall: { '& .MuiInputBase-input': { padding: '11px' } },
            multiline: {
                '& .MuiInputBase-input': {
                    padding: 0,
                    resize: 'none',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    '&:hover, &:focus': {
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': { display: 'block' },
                    },
                },
            },
        },
    },
    MuiFormControlLabel: {
        styleOverrides: {
            root: {
                gap: varnishTokens.spacing[1].value,
                marginLeft: 0,
                marginRight: 0,
            },
        },
    },
    MuiFormHelperText: {
        styleOverrides: {
            root: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    fontSize: varnishTokens.font.size.sm.value,
                    marginTop: '4px',
                    '&.Mui-error': { color: palette.form.input.error.contrast },
                    '&.Mui-disabled': { color: palette.form.input.disabled.contrast },
                };
            },
            sizeSmall: {
                fontSize: '12px', // we don't have a font size this small in the theme, consider adding xs? .75rem
            },
            // @ts-expect-error — augmentation not visible to tsup DTS build
            sizeLarge: { fontSize: varnishTokens.font.size.md.value },
        },
    },
    MuiInput: {
        styleOverrides: {
            root: ({ theme }) => ({
                '&:hover:not(.Mui-disabled):before': {
                    borderBottomColor: (theme.vars ?? theme).palette.form.input.hovered.stroke,
                },
            }),
            input: { padding: '7px 0' },
            sizeSmall: { '& .MuiInputBase-input': { padding: '5px 0' } },
        },
    },
    MuiInputBase: {
        styleOverrides: {
            root: {
                lineHeight: varnishTokens.lineHeight[3].value,
            },
            sizeSmall: {
                fontSize: '12px', // we don't have a font size this small in the theme, consider adding xs? .75rem
                lineHeight: varnishTokens.lineHeight[2].value,
            },
        },
    },
    MuiInputLabel: {
        styleOverrides: {
            root: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    position: 'relative',
                    transform: 'none',
                    marginBottom: '4px',
                    fontSize: varnishTokens.font.size.sm.value,
                    color: palette.text.secondary,
                    '&.Mui-focused': { color: palette.text.secondary },
                    '&.Mui-error': { color: palette.form.input.error.contrast },
                    '&.Mui-disabled': { color: palette.form.input.disabled.contrast },
                };
            },
            sizeSmall: {
                fontSize: '12px', // we don't have a font size this small in the theme, consider adding xs? .75rem
            },
            // @ts-expect-error — augmentation not visible to tsup DTS build
            sizeLarge: { fontSize: varnishTokens.font.size.md.value },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    borderRadius: varnishTokens.radii.sm.value,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        borderColor: palette.form.input.stroke,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.form.input.hovered.stroke,
                    },
                    // Multiline (TextArea) padding on root
                    // Padding compensates for 1px border: visual size = padding + border
                    '&.MuiInputBase-multiline': { padding: '7px 11px' },
                    '&.MuiInputBase-multiline.MuiInputBase-sizeSmall': { padding: '11px' },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.form.input.error.stroke,
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.form.input.disabled.stroke,
                    },
                    '&.Mui-disabled': { cursor: 'not-allowed' },
                };
            },
            notchedOutline: {
                // Hide the legend/notch since label is now above
                '& legend': { display: 'none' },
            },
            // Padding compensates for 1px border: visual size = padding + border
            input: { padding: '7px 11px' },
            sizeSmall: { '& .MuiInputBase-input': { padding: '11px' } },
            multiline: {
                '& .MuiInputBase-input': {
                    padding: 0,
                    resize: 'none',
                    // Hide scrollbar until hover/focus
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    '&:hover, &:focus': {
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': { display: 'block' },
                    },
                },
            },
        },
    },
    MuiRadio: {
        defaultProps: {
            color: 'default',
        },
        styleOverrides: {
            root: {
                padding: 0,
                // Default (medium) icon size — MUI doesn't add a sizeMedium class
                // for the default size, so we set it here and let sizeSmall override.
                '& .MuiSvgIcon-root': { fontSize: varnishTokens.font.size.sm.value },
            },
            sizeSmall: {
                '& .MuiSvgIcon-root': {
                    fontSize: '0.75rem', // we don't have a font size this small in the theme, consider adding xs? .75rem
                },
            },
        },
        variants: [
            {
                props: { color: 'tertiary' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.tertiary.main,
                        '&.Mui-checked': { color: palette.tertiary.main },
                    };
                },
            },
            {
                props: { color: 'default' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.form.radio.stroke,
                        '&.Mui-checked': { color: palette.form.radio.checked.fill },
                        '&.Mui-disabled': { color: palette.form.radio.disabled.stroke },
                        '&.Mui-error': { color: palette.form.radio.error.stroke },
                    };
                },
            },
            {
                props: { size: 'large' },
                style: {
                    '& .MuiSvgIcon-root': { fontSize: varnishTokens.font.size.xl.value },
                },
            },
        ],
    },
    MuiSelect: {
        styleOverrides: {
            root: {
                fontWeight: varnishTokens.font.weight.medium.value,
                borderRadius: varnishTokens.radii.sm.value,
                // Medium (default) select padding
                '& .MuiSelect-select': {
                    padding: '8px 24px',
                    fontSize: varnishTokens.font.size.sm.value,
                },
                // Small select padding
                '&.MuiInputBase-sizeSmall .MuiSelect-select': {
                    padding: '8px 20px',
                    fontSize: '12px', // we don't have a font size this small in the theme, consider adding xs? .75rem
                },
                // Large select padding
                '&.MuiInputBase-sizeLarge .MuiSelect-select': {
                    padding: '16px 24px',
                    fontSize: varnishTokens.font.size.md.value,
                },
            },
        },
        variants: [
            {
                props: { shape: 'rounded' },
                style: {
                    borderRadius: varnishTokens.radii.full.value,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: varnishTokens.radii.full.value,
                    },
                },
            },
        ],
    },
    MuiSlider: {
        styleOverrides: {
            thumb: {
                width: 14,
                height: 14,
            },
            track: {
                height: 4,
                borderRadius: varnishTokens.radii.full.value,
                border: 'none',
            },
            rail: ({ theme }) => ({
                height: 4,
                borderRadius: varnishTokens.radii.full.value,
                backgroundColor: (theme.vars ?? theme).palette.form.slider.stroke,
            }),
            // Vertical: swap thickness to width, restore height
            root: {
                '&.MuiSlider-vertical .MuiSlider-track': { height: 'auto', width: 4 },
                '&.MuiSlider-vertical .MuiSlider-rail': { height: '100%', width: 4 },
            },
            sizeSmall: {
                '& .MuiSlider-thumb': { width: 12, height: 12 },
            },
        },
        variants: [
            {
                props: { color: 'tertiary' },
                style: ({ theme }) => ({ color: (theme.vars ?? theme).palette.tertiary.main }),
            },
            {
                props: { color: 'default' },
                style: ({ theme }) => ({ color: (theme.vars ?? theme).palette.default.main }),
            },
            {
                props: { size: 'large' },
                style: {
                    '& .MuiSlider-thumb': { width: 20, height: 20 },
                    '& .MuiSlider-track': { height: 6 },
                    '& .MuiSlider-rail': { height: 6 },
                    // Vertical large: use width for thickness
                    '&.MuiSlider-vertical .MuiSlider-track': { height: 'auto', width: 6 },
                    '&.MuiSlider-vertical .MuiSlider-rail': { height: '100%', width: 6 },
                },
            },
        ],
    },
    MuiSwitch: {
        defaultProps: {
            disableRipple: true,
            color: 'default',
        },
        styleOverrides: {
            root: ({ ownerState, theme }) => {
                const { palette } = theme.vars ?? theme;
                const colorKey = ownerState.color || 'primary';
                const p = palette[colorKey as keyof typeof palette] as
                    | { contrastText?: string }
                    | undefined;
                return {
                    padding: 0,
                    // Override checked thumb color to use contrastText
                    '& .MuiSwitch-switchBase.Mui-checked': p?.contrastText
                        ? { color: p.contrastText, '&:hover': { backgroundColor: 'transparent' } }
                        : {},
                    // Checked track: full opacity, no border
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        opacity: 1,
                        borderColor: 'transparent',
                    },
                    // Disabled state
                    '& .MuiSwitch-switchBase.Mui-disabled': {
                        color: palette.form.switch.disabled.stroke,
                        '& + .MuiSwitch-track': {
                            backgroundColor: palette.form.switch.disabled.fill,
                            borderColor: palette.form.switch.disabled.stroke,
                            opacity: 1,
                        },
                    },
                    '& .MuiSwitch-switchBase.Mui-disabled.Mui-checked': {
                        color: palette.form.switch.disabled.contrast,
                        '& + .MuiSwitch-track': { borderColor: 'transparent' },
                    },
                    // Error state
                    '& .MuiSwitch-switchBase.Mui-error': {
                        color: palette.form.switch.error.stroke,
                        '& + .MuiSwitch-track': {
                            backgroundColor: palette.form.switch.error.fill,
                            borderColor: palette.form.switch.error.stroke,
                        },
                    },
                    '& .MuiSwitch-switchBase.Mui-error.Mui-checked': {
                        color: palette.form.switch.error.contrast,
                    },
                };
            },
            switchBase: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    padding: '2px',
                    color: palette.form.switch.stroke,
                    '&:hover': { backgroundColor: 'transparent' },
                };
            },
            thumb: { boxShadow: 'none' },
            track: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    borderRadius: varnishTokens.radii.full.value,
                    border: '1px solid',
                    borderColor: palette.form.switch.stroke,
                    backgroundColor: palette.form.switch.fill,
                    opacity: 1,
                };
            },
            sizeSmall: {
                width: '1.375rem',
                height: '0.75rem',
                '& .MuiSwitch-switchBase': {
                    padding: '2px',
                    '&.Mui-checked': { transform: 'translateX(10px)' },
                },
                '& .MuiSwitch-thumb': { width: '0.5rem', height: '0.5rem' },
            },
            sizeMedium: {
                width: '1.625rem',
                height: '0.875rem',
                '& .MuiSwitch-switchBase': {
                    padding: '2px',
                    '&.Mui-checked': { transform: 'translateX(12px)' },
                },
                '& .MuiSwitch-thumb': { width: '0.625rem', height: '0.625rem' },
            },
        },
        variants: [
            {
                props: { color: 'tertiary' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: palette.tertiary.contrastText,
                            '& + .MuiSwitch-track': { backgroundColor: palette.tertiary.main },
                        },
                    };
                },
            },
            {
                props: { color: 'default' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: palette.form.switch.checked.contrast,
                            '& + .MuiSwitch-track': {
                                backgroundColor: palette.form.switch.checked.fill,
                            },
                        },
                    };
                },
            },
            {
                props: { size: 'large' },
                style: {
                    width: '2.3125rem',
                    height: '1.25rem',
                    '& .MuiSwitch-switchBase': {
                        padding: '3px',
                        '&.Mui-checked': { transform: 'translateX(17px)' },
                    },
                    '& .MuiSwitch-thumb': { width: '0.875rem', height: '0.875rem' },
                },
            },
        ],
    },
    MuiTextField: {
        defaultProps: {
            slotProps: { inputLabel: { shrink: true } },
        },
        variants: [
            {
                props: { size: 'large' },
                style: {
                    '& .MuiInputBase-root': {
                        fontSize: varnishTokens.font.size.lg.value,
                        lineHeight: varnishTokens.lineHeight[2].value,
                    },
                    '& .MuiInputBase-input': { padding: '15px' },
                    // Multiline large: padding on root only
                    '& .MuiInputBase-root.MuiInputBase-multiline': { padding: '15px' },
                    '& .MuiInputBase-inputMultiline': { padding: 0 },
                    '& .MuiInputLabel-root': { fontSize: varnishTokens.font.size.md.value },
                },
            },
            {
                props: { shape: 'rounded' },
                style: {
                    '& .MuiInputBase-root': {
                        // No matching radii token for 28px — flag for design review
                        borderRadius: '28px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': { borderWidth: '2px' },
                    '& .MuiInputBase-input': { paddingInline: '24px' },
                    // Multiline rounded: padding on root only
                    '& .MuiInputBase-root.MuiInputBase-multiline': { paddingInline: '24px' },
                    '& .MuiInputBase-inputMultiline': { paddingInline: 0 },
                },
            },
        ],
    },
};
